import { groupBy, isEmpty, repeat, differenceWith, isEqual } from 'lodash'
import { createSelector } from 'reselect'

import { showBookTabs } from '../helpers/characters'
import { isSeries } from '../helpers/books'
import { depth } from '../reducers/tree'
import { noEntityHasLegacyAttributeBound } from './noEntitiyHasValueBound'

// Other selector dependencies
import { allCardsSelector, singleCardSelector } from './cardsFirstOrder'
import { allBeatsSelector } from './beatsFirstOrder'
import {
  chosePlottrModeSelector,
  choseProModeSelector,
  previouslyLoggedIntoProSelector,
} from './settingsFirstOrder'
import { isLoggedInSelector, isOnWebSelector, userIdSelector } from './clientFirstOrder'
import { permissionSelector } from './permissionFirstOrder'
import { allBookIdsSelector, allBooksAsArraySelector, allBooksSelector } from './booksFirstOrder'
import {
  cardsCustomAttributesSelector,
  noteCustomAttributesSelector,
  placeCustomAttributesSelector,
  characterCustomAttributesSelector,
} from './customAttributesFirstOrder'
import { attributesSelector } from './attributesFirstOrder'
import { allNotesSelector } from './notesFirstOrder'
import { allLinesSelector } from './linesFirstOrder'
import { fullFileStateSelector } from './fullFileFirstOrder'
import {
  hasActiveProLicenseSelector,
  needsToCheckPlottrLicense,
  needsToCheckProLicense,
  plottrLicenseSelector,
  proLicenseSelector,
} from './licenseFirstOrder'
import { isOfflineSelector } from './projectFirstOrder'
import { initialFamilyTreePosition } from '../store/initialState'
import { allFamilyTreesSelector } from './familyTreeFirstOrder'
import { allPlacesSelector } from './placesFirstOrder'
import { allCharactersSelector } from './charactersFirstOrder'
import { ideaBoard as initialIdeaBoard } from '../store/initialState'
import { forceCheckSelector } from './applicationStateFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').PlottrFileUi} PlottrFileUi
 * @typedef {import('../index').Line} Line
 * @typedef {import('../index').Note} Note
 * @typedef {import('../index').Attribute} Attribute
 * @typedef {import('../index').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('../types/plottr').FamilyTreeStagePosition} FamilyTreeStagePosition
 * @typedef {import('../types/plottr').PreviewPanels} PreviewPanels
 * @typedef {import('../types/plottr').PreviewPanelItem} PreviewPanelItem
 * @typedef {import('../types/plottr').Bookmarks} Bookmarks
 * @typedef {import('../types/plottr').BookmarkItem} BookmarkItem
 * @typedef {import('../types/plottr').BookmarkEntityKind} BookmarkEntityKind
 * @typedef {import('../types/plottr').QuickEditModal} QuickEditModalSettings
 * @typedef {import('../types/plottr').IdeaConfirmModal} IdeaConfirmModal
 * @typedef {import('../types/plottr').IdeaModal} IdeaModal
 * @typedef {import('../types/plottr').IdeaItem} IdeaItem
 * @typedef {import('../types/plottr').IdeaBoard} IdeaBoard
 * @typedef {import('../types/plottr').HierarchyLevel} HierarchyLevel
 * @typedef {import('../types/plottr').HierarchyLevelsPlottrFile} HierarchyLevelsPlottrFile
 * @typedef {import('../types/plottr').TimelineScrollPosition} TimelineScrollPosition
 * @typedef {import('../types/plottr').PlottrFileBeatsEntry} PlottrFileBeatsEntry
 * @typedef {import('../types/plottr').HierarchyLevels} HierarchyLevels
 * @typedef {import('../types/plottr').UiCollaborators} UiCollaborators
 * @typedef {import('../types/plottr').CharacterFilter} CharacterFilter
 * @typedef {import('../types/plottr').NoteFilter} NoteFilter
 * @typedef {import('../types/plottr').TimelineFilter} TimelineFilter
 * @typedef {import('../types/plottr').TimelineUi} TimelineUi
 * @typedef {import('../types/plottr').PlottrTab} PlottrTab
 * @typedef {import('../types/plottr').SearchTerms} SearchTerms
 * @typedef {import('../types/plottr').ActView} ActView
 * @typedef {import('../types/plottr').TimelineOrientation} TimelineOrientation
 * @typedef {import('../types/plottr').ActConfigModalUi} ActConfigModalUi
 * @typedef {import('../types/plottr').CharacterTabUi} CharacterTabUi
 * @typedef {import('../types/plottr').CharacterView} CharacterView
 * @typedef {import('../types/plottr').CustomAttributeOrderUiPlottrFile} CustomAttributeOrderUiPlottrFile
 * @typedef {import('../types/plottr').AttributeOrderEntry} AttributeOrderEntry
 * @typedef {import('../types/plottr').TemplateModalUi} TemplateModalUi
 * @typedef {import('../types/plottr').CardDialogUi} CardDialogUi
 * @typedef {import('../types/plottr').PlaceFilter} PlaceFilter
 * @typedef {import('../types/plottr').OutlineFilter} OutlineFilter
 * @typedef {import('../types/plottr').OutlineTabUi} OutlineTabUi
 * @typedef {import('../types/plottr').OutlineTabUiLayout} OutlineTabUiLayout
 * @typedef {import('../types/plottr').OutlineFulltextUiLayout} OutlineFulltextUiLayout
 * @typedef {import('../types/plottr').OutlineTabView} OutlineTabView
 * @typedef {import('../types/plottr').TextAlignment} TextAlignment
 * @typedef {import('../types/plottr').OutlineFulltextHoverDetails} OutlineFulltextHoverDetails
 * @typedef {import('../types/plottr').OutlineTabCardUiLayout} OutlineTabCardUiLayout
 * @typedef {import('../types/plottr').CardColorSource} CardColorSource
 * @typedef {import('../types/plottr').FamilyTreeUi} FamilyTreeUi
 * @typedef {import('../types/plottr').AttributeTab} AttributeTab
 * @typedef {import('../types/plottr').PlottrThemes} PlottrThemes
 * @typedef {import('../types/plottr-in-your-pocket').UniversalIdeaId} UniversalIdeaId
 */

/**
 * @type {function(PlottrState): boolean}
 */
export const shouldBeInProSelector = createSelector(
  previouslyLoggedIntoProSelector,
  isOnWebSelector,
  (previouslyLoggedIntoPro, isOnWeb) => {
    return !!(previouslyLoggedIntoPro || isOnWeb)
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isInOfflineModeSelector = createSelector(
  isOfflineSelector,
  shouldBeInProSelector,
  (isOffline, shouldBeInPro) => {
    return isOffline && shouldBeInPro
  }
)

/**
 * @type {function(PlottrState): PlottrFileUi}
 */
export const rootUiSelector = createSelector(fullFileStateSelector, ({ ui }) => ui ?? {})
/**
 * @type {function(PlottrState): (UiCollaborators | Null)}
 */
export const uiCollaboratorsSelector = createSelector(rootUiSelector, (rootUi) => {
  return rootUi.collaborators
})
/**
 * @type {function(PlottrState): PlottrFileUi}
 */
export const uiSelector = createSelector(
  shouldBeInProSelector,
  userIdSelector,
  permissionSelector,
  rootUiSelector,
  (shouldBeLoggedIn, userId, permission, ui) => {
    if (!shouldBeLoggedIn) {
      return ui
    } else if (!userId || !permission) {
      return ui
    } else if (permission === 'owner') {
      return ui
    } else if (permission === 'collaborator') {
      const existingUI = ui.collaborators?.collaborators?.find((collaborator) => {
        return collaborator?.id === userId
      })

      /**
       * @type {PlottrFileUi}
       */
      // NOTE(ed): A collaborator UI object is the same as the root,
      // except that it itself doesn't have collaborators.  So we'll
      // ignore that type blip in favour of easier code for the
      // primary editor.
      //
      // @ts-ignore
      const result = existingUI ?? ui
      return result
    } else if (permission === 'viewer') {
      const existingUI = ui.collaborators?.viewers?.find((viewer) => {
        return viewer?.id === userId
      })

      /**
       * @type {PlottrFileUi}
       */
      // @ts-ignore
      const result = existingUI ?? ui
      return result
    } else {
      return ui
    }
  }
)

/**
 * @type {function(PlottrState): (number | 'series')}
 */
export const currentTimelineSelector = createSelector(
  uiSelector,
  allBookIdsSelector,
  (ui, bookIds) => {
    const currentTimeline = ui.currentTimeline
    if (currentTimeline == 'series') return currentTimeline
    if (bookIds.includes(currentTimeline)) {
      return currentTimeline
    } else {
      return bookIds[0] ?? 1
    }
  }
)

/**
 * @type {function(PlottrState): (CharacterFilter | Null)}
 */
export const characterFilterSelector = createSelector(uiSelector, ({ characterFilter }) => {
  return characterFilter
})
/**
 * @type {function(PlottrState): String}
 */
export const characterSortSelector = createSelector(uiSelector, ({ characterSort }) => {
  return characterSort
})
/**
 * @type {function(PlottrState): (NoteFilter | Null)}
 */
export const noteFilterSelector = createSelector(uiSelector, ({ noteFilter }) => {
  return noteFilter
})
/**
 * @type {function(PlottrState): String}
 */
export const noteSortSelector = createSelector(uiSelector, ({ noteSort }) => {
  return noteSort
})
/**
 * @type {function(PlottrState): (PlaceFilter | Null)}
 */
export const placeFilterSelector = createSelector(uiSelector, ({ placeFilter }) => {
  return placeFilter
})
/**
 * @type {function(PlottrState): String}
 */
export const placeSortSelector = createSelector(uiSelector, ({ placeSort }) => {
  return placeSort
})
/**
 * @type {function(PlottrState): (TimelineFilter | Null)}
 */
export const timelineFilterSelector = createSelector(uiSelector, ({ timelineFilter }) => {
  return timelineFilter
})
/**
 * @type {function(PlottrState): OutlineFilter}
 */
export const outlineFilterSelector = createSelector(uiSelector, ({ outlineFilter }) => {
  return outlineFilter
})
/**
 * @type {function(PlottrState): TimelineUi}
 */
export const timelineSelector = createSelector(uiSelector, ({ timeline }) => {
  return timeline
})
/**
 * @type {function(PlottrState): Array<Number>}
 */
export const characterCategoryFilterSelector = createSelector(characterFilterSelector, (filter) => {
  return filter?.category || []
})
/**
 * @type {function(PlottrState): Array<Number>}
 */
export const placeCategoryFilterSelector = createSelector(placeFilterSelector, (filter) => {
  return filter?.category || []
})
/**
 * @type {function(PlottrState): Array<Number>}
 */
export const noteCategoryFilterSelector = createSelector(noteFilterSelector, (filter) => {
  return filter?.category || []
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const editingBeatTitleIdSelector = createSelector(timelineSelector, ({ editingBeatId }) => {
  return editingBeatId
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const contextMenuBeatTimelineSelector = createSelector(
  timelineSelector,
  ({ contextMenuBeat }) => {
    return contextMenuBeat
  }
)
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const timelineBeatToDeleteSelector = createSelector(timelineSelector, ({ beatToDelete }) => {
  return beatToDelete
})
/**
 * @type {function(PlottrState): ('small' | 'medium' | 'large')}
 */
export const timelineSizeSelector = createSelector(timelineSelector, ({ size }) => {
  return size
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isSmallSelector = createSelector(timelineSizeSelector, (size) => {
  return size == 'small'
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isMediumSelector = createSelector(timelineSizeSelector, (size) => {
  return size === 'medium'
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isLargeSelector = createSelector(timelineSizeSelector, (size) => {
  return size == 'large'
})
/**
 * @type {function(PlottrState): TimelineScrollPosition}
 */
export const timelineScrollPositionSelector = createSelector(
  uiSelector,
  ({ timelineScrollPosition }) => {
    return timelineScrollPosition
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isNotesManuallySortedSelector = createSelector(noteSortSelector, (sort) => {
  return sort == 'manual'
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isCharactersManuallySortedSelector = createSelector(characterSortSelector, (sort) => {
  return sort == 'manual'
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isPlacesManuallySortedSelector = createSelector(placeSortSelector, (sort) => {
  return sort == 'manual'
})

/**
 * @type {function(PlottrState): boolean}
 */
export const attributesDialogIsOpenSelector = createSelector(
  uiSelector,
  ({ attributesDialogIsOpen }) => {
    return attributesDialogIsOpen
  }
)
/**
 * @type {function(PlottrState): PlottrTab}
 */
export const currentViewSelector = createSelector(uiSelector, ({ currentView }) => {
  return currentView
})

/**
 * @type {function(PlottrState): boolean}
 */
export const isSeriesSelector = createSelector(currentTimelineSelector, isSeries)

/**
 * @type {function(PlottrState): boolean}
 */
export const timelineFilterIsEmptySelector = createSelector(
  timelineFilterSelector,
  (filter) => filter == null || Object.keys(filter).every((key) => !filter[key].length)
)

/**
 * @type {function(PlottrState): SearchTerms}
 */
export const searchTermSelector = createSelector(uiSelector, ({ searchTerms }) => {
  return searchTerms
})
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const notesSearchTermSelector = createSelector(searchTermSelector, ({ notes }) => {
  return notes
})
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const charactersSearchTermSelector = createSelector(searchTermSelector, ({ characters }) => {
  return characters
})
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const placesSearchTermSelector = createSelector(searchTermSelector, ({ places }) => {
  return places
})
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const tagsSearchTermSelector = createSelector(searchTermSelector, ({ tags }) => {
  return tags
})
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const outlineSearchTermSelector = createSelector(searchTermSelector, ({ outline }) => {
  return outline
})
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const timelineSearchTermSelector = createSelector(searchTermSelector, ({ timeline }) => {
  return timeline
})

/**
 * @type {function(PlottrState): (String | Null)}
 */
export const ideasSearchTermSelector = createSelector(searchTermSelector, ({ ideas }) => {
  return ideas
})

/**
 * @type {function(PlottrState): String | null}
 */
export const bookmarksSearchTermSelector = createSelector(searchTermSelector, ({ bookmarks }) => {
  return bookmarks
})

/**
 * @type {function(PlottrState): IdeaBoard}
 */
export const ideaBoardSelector = createSelector(uiSelector, ({ ideaBoard }) => {
  return !isEmpty(ideaBoard) ? ideaBoard : initialIdeaBoard
})

/**
 * @type {function(PlottrState): boolean}
 */
export const isIdeaBoardOpenSelector = createSelector(ideaBoardSelector, ({ open }) => {
  return open ?? false
})

/**
 * @type {function(PlottrState): Array<IdeaItem>}
 */
export const allIdeasSelector = createSelector(ideaBoardSelector, ({ ideas }) => {
  return ideas ?? []
})

/**
 * @type {function(PlottrState): Array<UniversalIdeaId>}
 */
export const ideasFromPocketThatWereUsedSelector = createSelector(
  ideaBoardSelector,
  ({ pocketIdeasUsed }) => {
    return pocketIdeasUsed
  }
)

/**
 * @type {function(PlottrState, number): number}
 */
const selectId = (_state, id) => id

/**
 * @type {function(PlottrState, number): (IdeaItem | Null)}
 */
export const singleIdeaSelector = createSelector(allIdeasSelector, selectId, (allIdeas, propId) => {
  return allIdeas.find((idea) => idea.id == propId) ?? null
})

/**
 * @type {function(PlottrState): IdeaModal}
 */
export const ideaBoardModalSelector = createSelector(
  ideaBoardSelector,
  (ideaBoard) => ideaBoard.modal
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isIdeaBoardModalOpenSelector = createSelector(
  ideaBoardModalSelector,
  (ideaBoard) => ideaBoard?.open ?? false
)

/**
 * @type {function(PlottrState): number | Null}
 */
export const ideaBoardModalIdSelector = createSelector(
  ideaBoardModalSelector,
  (ideaBoardModal) => ideaBoardModal?.id
)

/**
 * @type {function(PlottrState): IdeaConfirmModal}
 */
export const ideaConfirmModalSelector = createSelector(ideaBoardSelector, (ideaBoard) => {
  return ideaBoard?.confirmModal ?? {}
})

/**
 * @type {function(PlottrState): boolean}
 */
export const isConfirmMoveModalOpenSelector = createSelector(
  ideaConfirmModalSelector,
  (ideaConfirmModal) =>
    (ideaConfirmModal?.open &&
      ideaConfirmModal?.section !== 'cards' &&
      !ideaConfirmModal?.isDeleting) ??
    false
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isConfirmToDeleteOpenSelector = createSelector(
  ideaConfirmModalSelector,
  (ideaConfirmModal) => (ideaConfirmModal?.open && ideaConfirmModal?.isDeleting) ?? false
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isTempCardModalOpenSelector = createSelector(
  ideaConfirmModalSelector,
  (ideaConfirmModal) => ideaConfirmModal?.open && ideaConfirmModal?.section === 'cards'
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isConfirmMoveToCardOpenSelector = createSelector(
  ideaConfirmModalSelector,
  isTempCardModalOpenSelector,
  (ideaConfirmModal, isCardModalOpen) =>
    (!ideaConfirmModal?.open && ideaConfirmModal?.section === 'cards' && isCardModalOpen) ?? false
)

/**
 * @type {function(PlottrState): boolean}
 */
export const shouldShowPanelsWithIdeaBoardSelector = createSelector(
  ideaBoardSelector,
  ({ shouldShowPanelsWithIdeaBoard }) => shouldShowPanelsWithIdeaBoard ?? false
)

/**
 * @type {function(PlottrState): boolean}
 */
export const showCollapsibleIdeaCardDescriptionSelector = createSelector(
  ideaBoardSelector,
  ({ showCardDescription }) => showCardDescription ?? true
)

/**
 * @type {function(PlottrState): PreviewPanels}
 */
export const previewPanelsSelector = createSelector(uiSelector, ({ previewPanels }) => {
  return previewPanels ?? {}
})

/**
 * @type {function(PlottrState): Array<PreviewPanelItem>}
 */
export const allPreviewPanelsAsFlatArraySelector = createSelector(
  previewPanelsSelector,
  ({ panels }) => {
    return panels ?? []
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const thereArePinnedEntitiesSelector = createSelector(
  allPreviewPanelsAsFlatArraySelector,
  (pinnedPanels) => {
    return !!pinnedPanels.length
  }
)

/**
 * @type {function(PlottrState): Array<PreviewPanelItem>}
 */
export const maximisedPreviewPanelsSelector = createSelector(
  allPreviewPanelsAsFlatArraySelector,
  (panels) => {
    return panels.filter((item) => item.coords.kind !== 'MINIMISED_PREVIEW_PANEL_POSITION')
  }
)

/**
 * @type {function(PlottrState): Array<PreviewPanelItem>}
 */
export const minimizedPreviewPanelsSelector = createSelector(
  allPreviewPanelsAsFlatArraySelector,
  (panels) => {
    return panels.filter((item) => item.coords.kind === 'MINIMISED_PREVIEW_PANEL_POSITION')
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const showPreviewPanelConfirmModalSelector = createSelector(
  previewPanelsSelector,
  ({ showConfirmModal }) => Boolean(showConfirmModal)
)

/**
 * @type {function(PlottrState): QuickEditModalSettings}
 */
export const quickEditModalSelector = createSelector(uiSelector, ({ quickEditModal }) => {
  return quickEditModal ?? quickEditModal
})

/**
 * @type {function(PlottrState): boolean}
 */
export const isQuickEditModalOpenSelector = createSelector(
  quickEditModalSelector,
  ({ open, kind, id }) => (open && kind !== null && id !== null) ?? false
)

/**
 * @type {function(PlottrState): ActView}
 */
export const selectedTimelineViewSelector = createSelector(timelineSelector, ({ view }) => {
  return view ?? 'default'
})

/**
 * @param {Array<Line>} lines
 * @param {number | 'series'}  bookId
 */
const countPinnedLines = (lines, bookId) => {
  return lines.reduce((pinnedCount, line) => {
    return pinnedCount + (line.bookId === bookId && line.isPinned ? 1 : 0)
  }, 0)
}

/**
 * @param {PlottrState} _state
 * @param {number | 'series'} id
 * @return {number | 'series'}
 */
const bookIdSelector = (_state, id) => id
/**
 * @type {function(PlottrState, number | 'series'): number}
 */
export const pinnedPlotlinesForAnotherBookSelector = createSelector(
  allLinesSelector,
  bookIdSelector,
  countPinnedLines
)

/**
 * @type {function(PlottrState): number}
 */
export const pinnedPlotlinesSelector = createSelector(
  allLinesSelector,
  currentTimelineSelector,
  countPinnedLines
)

/**
 * @type {function(PlottrState): HierarchyLevelsPlottrFile}
 */
export const allHierarchyLevelsSelector = createSelector(
  fullFileStateSelector,
  ({ hierarchyLevels }) => hierarchyLevels ?? {}
)
/**
 * @type {function(PlottrState): HierarchyLevels}
 */
export const hierarchyLevelsSelector = createSelector(
  allHierarchyLevelsSelector,
  currentTimelineSelector,
  (allLevels, timeline) => {
    return allLevels[timeline]
  }
)
/**
 * @type {function(PlottrState): number}
 */
export const hierarchyLevelCount = createSelector(hierarchyLevelsSelector, (hierarchyLevels) => {
  return hierarchyLevels ? Object.keys(hierarchyLevels).length : 1
})
/**
 * @type {function(PlottrState): ActView}
 */
export const timelineViewSelector = createSelector(
  selectedTimelineViewSelector,
  hierarchyLevelCount,
  (selectedView, levelsOfHierarchy) => {
    if (selectedView === 'tabbed' && levelsOfHierarchy < 2) {
      return 'default'
    }
    return selectedView
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const timelineViewIsTabbedSelector = createSelector(timelineViewSelector, (view) => {
  return view === 'tabbed'
})
/**
 * @type {function(PlottrState): boolean}
 */
export const timelineViewIsStackedSelector = createSelector(timelineViewSelector, (view) => {
  return view === 'stacked'
})
/**
 * @type {function(PlottrState): number}
 */
export const timelineSelectedTabSelector = createSelector(timelineSelector, ({ actTab }) => {
  return actTab ?? 0
})
/**
 * @type {function(PlottrState): boolean}
 */
export const timelineViewIsDefaultSelector = createSelector(timelineViewSelector, (view) => {
  return view === 'default'
})
/**
 * @type {function(PlottrState): boolean}
 */
export const timelineViewIsntDefaultSelector = createSelector(timelineViewSelector, (view) => {
  return view !== 'default'
})

/**
 * @type {function(PlottrState): TimelineOrientation}
 */
const selectedOrientationSelector = createSelector(
  uiSelector,
  ({ orientation }) => orientation ?? 'horizontal'
)

/**
 * @type {function(PlottrState): number}
 */
export const stickyHeaderCountSelector = createSelector(
  pinnedPlotlinesSelector,
  timelineViewIsStackedSelector,
  hierarchyLevelCount,
  selectedOrientationSelector,
  (pinnedPlotlines, timelineViewIsStacked, hierarchyLevelCount, selectedOrientation) => {
    if (selectedOrientation == 'vertical') {
      return 1
    }
    if (timelineViewIsStacked) {
      return hierarchyLevelCount + pinnedPlotlines
    } else if (pinnedPlotlines) {
      return pinnedPlotlines + 1
    } else {
      return 1
    }
  }
)

/**
 * @type {function(PlottrState): number}
 */
export const stickyLeftColumnCountSelector = createSelector(
  pinnedPlotlinesSelector,
  timelineViewIsStackedSelector,
  hierarchyLevelCount,
  selectedOrientationSelector,
  (pinnedPlotlines, timelineViewIsStacked, hierarchyLevelCount, selectedOrientation) => {
    if (selectedOrientation == 'horizontal' || timelineViewIsStacked) {
      return 1
    } else if (pinnedPlotlines) {
      return pinnedPlotlines + 1
    } else {
      return 1
    }
  }
)

/**
 * @type {function(PlottrState): ActConfigModalUi}
 */
export const actConfigModalSelector = createSelector(uiSelector, ({ actConfigModal }) => {
  return actConfigModal ?? {}
})
/**
 * @type {function(PlottrState): boolean}
 */
export const actConfigModalIsOpenSelector = createSelector(actConfigModalSelector, ({ open }) => {
  return open
})

/**
 * @type {function(PlottrState): CharacterTabUi}
 */
export const characterTabSelector = createSelector(uiSelector, ({ characterTab }) => {
  return characterTab ?? {}
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const selectedCharacterSelector = createSelector(
  characterTabSelector,
  ({ selectedCharacter }) => {
    if (selectedCharacter || selectedCharacter === 0) {
      return selectedCharacter
    }

    return null
  }
)
/**
 * @type {function(PlottrState): CharacterView}
 */
export const characterViewSelector = createSelector(characterTabSelector, ({ view }) => view)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterViewIsFamilyTreeSelector = createSelector(
  characterTabSelector,
  ({ view }) => view == 'tree'
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isCharactersTabListViewSelector = createSelector(
  characterTabSelector,
  ({ showAsList }) => showAsList ?? true
)
/**
 * @type {function(PlottrState): CustomAttributeOrderUiPlottrFile}
 */
export const customAttributeOrderSelector = createSelector(
  uiSelector,
  ({ customAttributeOrder }) => {
    return customAttributeOrder
  }
)
/**
 * @type {function(PlottrState): Array<AttributeOrderEntry>}
 */
export const characterCustomAttributeOrderSelector = createSelector(
  customAttributeOrderSelector,
  ({ characters }) => characters ?? []
)

/**
 * @type {function(PlottrState): TemplateModalUi}
 */
const templateModalSelector = createSelector(uiSelector, ({ templateModal }) => {
  return templateModal
})
/**
 * @type {function(PlottrState): boolean}
 */
export const templateModalAdvancedPanelOpenSelector = createSelector(
  templateModalSelector,
  ({ expanded }) => {
    return !!expanded
  }
)

/**
 * @type {function(PlottrState): CardDialogUi}
 */
const cardDialogSelector = createSelector(uiSelector, ({ cardDialog }) => {
  return cardDialog
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isCardDialogDeletingSelector = createSelector(cardDialogSelector, ({ deleting }) => {
  return deleting
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isCardDialogColorPickerOpenSelector = createSelector(
  cardDialogSelector,
  ({ showColorPicker }) => {
    return showColorPicker
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isCardDialogTemplatePickerOpenSelector = createSelector(
  cardDialogSelector,
  ({ showTemplatePicker }) => {
    return showTemplatePicker
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isCardDialogTemplateBeingRemoved = createSelector(
  cardDialogSelector,
  ({ removeWhichTemplate }) => {
    return !!removeWhichTemplate
  }
)
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const whichTemplateIsBeingRemovedViaCardDialogSelector = createSelector(
  cardDialogSelector,
  ({ removeWhichTemplate }) => {
    return removeWhichTemplate
  }
)
/**
 * @type {function(PlottrState): number}
 */
export const cardDialogTabSelector = createSelector(cardDialogSelector, ({ activeTab }) => {
  return activeTab ?? 1
})

/**
 * @type {function(PlottrState): Array<String>}
 */
export const cardsCustomAttributesThatCanChangeSelector = createSelector(
  allCardsSelector,
  cardsCustomAttributesSelector,
  noEntityHasLegacyAttributeBound
)

/**
 * @type {function(PlottrState): Array<String>}
 */
export const notesCustomAttributesThatCanChangeSelector = createSelector(
  allNotesSelector,
  noteCustomAttributesSelector,
  noEntityHasLegacyAttributeBound
)

/**
 * @type {function(PlottrState): PlottrFileBeatsEntry}
 */
export const beatsByBookSelector = createSelector(
  allBeatsSelector,
  currentTimelineSelector,
  (beats, bookId) => {
    return beats[bookId]
  }
)

/**
 * @type {function(PlottrState): Array<HierarchyLevel>}
 */
export const sortedHierarchyLevels = createSelector(
  hierarchyLevelCount,
  hierarchyLevelsSelector,
  (levels, hierarchyLevels) => {
    const sortedLevels = []
    for (let i = 0; i < levels; ++i) {
      sortedLevels.push(hierarchyLevels[i])
    }
    return sortedLevels
  }
)
/**
 * @param {PlottrState} _state
 * @param {number} beatId
 * @return {number}
 */
const beatIdSelector = (_state, beatId) => beatId
/**
 * @type {function(PlottrState, number): boolean}
 */
export const beatExistsSelector = createSelector(
  beatsByBookSelector,
  beatIdSelector,
  (beats, beatId) => {
    return typeof beats.index[beatId] === 'object'
  }
)
/**
 * @type {function(PlottrState, number): HierarchyLevel}
 */
export const hierarchyLevelSelector = createSelector(
  beatsByBookSelector,
  beatIdSelector,
  sortedHierarchyLevels,
  beatExistsSelector,
  (beats, beatId, hierarchyLevels, beatExists) => {
    return (
      (beatExists && hierarchyLevels[depth(beats, beatId)]) ||
      hierarchyLevels[hierarchyLevels.length - 1]
    )
  }
)
/**
 * @type {function(PlottrState, number | 'series'): HierarchyLevels}
 */
export const hierarchyLevelsForAnotherBookSelector = createSelector(
  allHierarchyLevelsSelector,
  bookIdSelector,
  (hierarchies, bookId) => {
    return hierarchies[bookId]
  }
)

/**
 * @type {function(PlottrState, number): boolean}
 */
export const atMaximumHierarchyDepthSelector = createSelector(
  beatsByBookSelector,
  beatIdSelector,
  sortedHierarchyLevels,
  beatExistsSelector,
  (beats, beatId, hierarchyLevels, beatExists) => {
    return beatExists && depth(beats, beatId) === hierarchyLevels.length - 1
  }
)

/**
 * @type {function(PlottrState, number): String | Null}
 */
export const hierarchyLevelNameSelector = createSelector(
  beatsByBookSelector,
  beatIdSelector,
  sortedHierarchyLevels,
  beatExistsSelector,
  (beats, beatId, hierarchyLevels, beatExists) => {
    if (!beatId || !beatExists) {
      return hierarchyLevels[0]?.name ?? null
    }
    return (
      (hierarchyLevels[depth(beats, beatId)] || hierarchyLevels[hierarchyLevels.length - 1])
        ?.name ??
      hierarchyLevels[0]?.name ??
      null
    )
  }
)

/**
 * @type {function(PlottrState, number): String | null}
 */
export const beatInsertControlHierarchyLevelNameSelector = createSelector(
  beatsByBookSelector,
  beatIdSelector,
  sortedHierarchyLevels,
  timelineViewIsTabbedSelector,
  timelineViewIsDefaultSelector,
  beatExistsSelector,
  (beats, beatId, hierarchyLevels, timelineViewIsTabbed, timelineViewIsDefault, beatExists) => {
    if (timelineViewIsTabbed) {
      if (!beatExists || depth(beats, beatId) === 0) {
        return (hierarchyLevels[1] || hierarchyLevels[0]).name ?? null
      } else if (!beatId) {
        return (hierarchyLevels[2] || hierarchyLevels[1] || hierarchyLevels[0]).name ?? null
      }
    }

    if (!beatExists || (timelineViewIsDefault && !beatId)) {
      return hierarchyLevels[0].name ?? null
    } else {
      return (
        (hierarchyLevels[depth(beats, beatId)] || hierarchyLevels[hierarchyLevels.length - 1])
          ?.name ?? null
      )
    }
  }
)

/**
 * @type {function(PlottrState, number): (String | Null)}
 */
export const hierarchyChildLevelNameSelector = createSelector(
  beatsByBookSelector,
  beatIdSelector,
  sortedHierarchyLevels,
  beatExistsSelector,
  (beats, beatId, hierarchyLevels, beatExists) => {
    if (!beatId || !beatExists) return (hierarchyLevels[1] || hierarchyLevels[0]).name
    const newDepth = depth(beats, beatId) + 1
    const level = hierarchyLevels[newDepth]
    if (level) {
      return level.name
    } else {
      return `${repeat('Sub-', newDepth - hierarchyLevels.length + 1)}${
        (hierarchyLevels[hierarchyLevels.length - 1] || { name: '' })?.name
      }`
    }
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const placeFilterIsEmptySelector = createSelector(
  placeFilterSelector,
  placeCustomAttributesSelector,
  (filter, attributes) => {
    if (!filter) return true
    const allAttributes = [{ name: 'tag' }, { name: 'book' }, { name: 'category' }, ...attributes]
    return !allAttributes.some((attr) => filter[attr.name] && filter[attr.name].length)
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const noteFilterIsEmptySelector = createSelector(
  noteFilterSelector,
  noteCustomAttributesSelector,
  (filter, attributes) => {
    if (!filter) return true
    const allAttributes = [
      { name: 'place' },
      { name: 'tag' },
      { name: 'book' },
      { name: 'category' },
      { name: 'character' },
      { name: 'category' },
      ...attributes,
    ]
    return !allAttributes.some((attr) => filter[attr.name] && filter[attr.name].length)
  }
)

/**
 * @type {function(PlottrState): (String | Null)}
 */
export const topLevelBeatNameSelector = createSelector(sortedHierarchyLevels, (levels) => {
  return levels[0].name
})

/**
 * @type {function(PlottrState): (TimelineFilter | CharacterFilter | PlaceFilter | NoteFilter | Null)}
 */
export const filteredItemsSelector = createSelector(
  currentViewSelector,
  timelineFilterSelector,
  characterFilterSelector,
  placeFilterSelector,
  noteFilterSelector,
  (currentView, timelineFilter, characterFilter, placeFilter, noteFilter) => {
    switch (currentView) {
      case 'timeline':
        return timelineFilter
      case 'places':
        return placeFilter
      case 'characters':
        return characterFilter
      case 'notes':
        return noteFilter
      default:
        return {
          book: [],
          category: [],
          tag: [],
        }
    }
  }
)

/**
 * @type {function(PlottrState, number): (Line | Null)}
 */
export const cardsLineSelector = createSelector(
  singleCardSelector,
  allLinesSelector,
  (card, lines) => {
    if (!card) {
      return null
    }

    return (
      lines.find(({ id }) => {
        return card.lineId === id
      }) || null
    )
  }
)

/**
 * NOTE(ed): there may not be any lines in the file, so this could
 * produce null.
 *
 * @type {function(PlottrState, number): (Line | Null)}
 */
export const cardsLineOrDefaultSelector = createSelector(
  cardsLineSelector,
  allLinesSelector,
  (line, lines) => {
    return line ?? lines[0] ?? null
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isLoggedIntoProWithActiveLicenseSelector = createSelector(
  isLoggedInSelector,
  previouslyLoggedIntoProSelector,
  hasActiveProLicenseSelector,
  isOnWebSelector,
  (isLoggedIn, shouldBeInPro, hasActiveProLicense, isOnWeb) => {
    return isLoggedIn && (isOnWeb || (shouldBeInPro && hasActiveProLicense))
  }
)

/**
 * @type {function(PlottrState, Date): boolean}
 */
export const needsToCheckALicenseType = createSelector(
  needsToCheckPlottrLicense,
  needsToCheckProLicense,
  isLoggedInSelector,
  forceCheckSelector,
  plottrLicenseSelector,
  proLicenseSelector,
  chosePlottrModeSelector,
  choseProModeSelector,
  (
    needsToCheckPlottr,
    needsToCheckPro,
    isLoggedIn,
    forceCheck,
    plottrLicense,
    proLicense,
    chosePlottrMode,
    choseProMode
  ) => {
    return (
      forceCheck ||
      needsToCheckPlottr ||
      needsToCheckPro ||
      (plottrLicense === null && chosePlottrMode) ||
      (proLicense === null && choseProMode) ||
      (!needsToCheckPro && !isLoggedIn)
    )
  }
)

/**
 * @type {function(PlottrState): OutlineTabUi}
 */
export const outlineTabSelector = createSelector(uiSelector, ({ outlineTab }) => {
  return outlineTab
})

/**
 * @type {function(PlottrState): OutlineTabUiLayout}
 */
export const outlineLayoutSelector = createSelector(outlineTabSelector, ({ layout }) => {
  return layout
})

/**
 * @type {function(PlottrState): OutlineFulltextUiLayout}
 */
export const outlineFulltextLayoutSelector = createSelector(
  outlineLayoutSelector,
  ({ fulltext }) => {
    return fulltext
  }
)

/**
 * @type {function(PlottrState): OutlineTabView}
 */
export const outlineViewSelector = createSelector(outlineLayoutSelector, ({ view }) => {
  return view ?? 'plan'
})

/**
 * @type {function(PlottrState): boolean}
 */
export const showOutlineFulltextCardTitleSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ showCardTitle }) => {
    return showCardTitle ?? true
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const showOutlineFulltextBeatTitleSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ showBeatTitle }) => {
    return showBeatTitle ?? true
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const showOutlineFulltextBeatGapsSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ showBeatGaps }) => {
    return showBeatGaps ?? false
  }
)

/**
 * @type {function(PlottrState): TextAlignment}
 */
export const fulltextCardTitleAlignmentSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ cardTitleAlignment }) => {
    return cardTitleAlignment ?? 'left'
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const outlineDetailsPinnedSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ detailsPinned }) => {
    return detailsPinned ?? false
  }
)

/**
 * @type {function(PlottrState): TextAlignment}
 */
export const fulltextBeatTitleAlignmentSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ beatTitleAlignment }) => {
    return beatTitleAlignment ?? 'left'
  }
)

/**
 * @type {function(PlottrState): String}
 */
export const outlineFulltextLightSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ light }) => {
    return light ?? '2'
  }
)

/**
 * @type {function(PlottrState): number}
 */
export const outlineFulltextZoomSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ zoom }) => {
    return Number(zoom ?? 1)
  }
)

/**
 * @type {function(PlottrState): String}
 */
export const outlineFulltextScaledHeightSelector = createSelector(
  outlineFulltextZoomSelector,
  (zoom) => {
    return zoom === 1.75
      ? '60%'
      : zoom === 1.5
        ? '70%'
        : zoom === 1.25
          ? '80%'
          : zoom === 1
            ? '90%'
            : zoom === 0.9
              ? '100%'
              : zoom === 0.8
                ? '110%'
                : zoom === 0.75
                  ? '120%'
                  : zoom === 0.5
                    ? '180%'
                    : '90%'
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const showFulltextPageLayoutConfigSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ showLayoutConfig }) => {
    return showLayoutConfig ?? true
  }
)

/**
 * @type {function(PlottrState): OutlineFulltextHoverDetails}
 */
export const outlineFulltextHoverDetailsSelector = createSelector(
  outlineFulltextLayoutSelector,
  ({ hoverDetails }) => {
    return hoverDetails ?? {}
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isFulltextHoverDetailsVisibleSelector = createSelector(
  outlineFulltextHoverDetailsSelector,
  ({ visible }) => {
    return visible ?? false
  }
)

/**
 * @type {function(PlottrState): OutlineTabCardUiLayout}
 */
export const outlineCardsSelector = createSelector(outlineLayoutSelector, ({ card }) => {
  return card
})

/**
 * @type {function(PlottrState): CardColorSource}
 */
export const outlineCardColorSourceSelector = createSelector(
  outlineCardsSelector,
  ({ colorSource }) => {
    return colorSource ?? 'plotline'
  }
)

/**
 * @type {function(PlottrState): FamilyTreeUi}
 */
const familyTreeUIStateSelector = createSelector(uiSelector, ({ familyTree }) => {
  return familyTree
})

/**
 * @type {function(PlottrState): number | Null}
 */
export const currentFamilyTreeIdSelector = createSelector(
  familyTreeUIStateSelector,
  allFamilyTreesSelector,
  ({ currentFamilyTree }, allFamilyTrees) => {
    if (typeof currentFamilyTree === 'number' && allFamilyTrees?.[currentFamilyTree]) {
      return currentFamilyTree
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): (number | Null)}
 */
export const familyTreeNameBeingEditedSelector = createSelector(
  familyTreeUIStateSelector,
  ({ idOfTreeWhoseNameIsBeingEdited }) => {
    return idOfTreeWhoseNameIsBeingEdited
  }
)

/**
 * @type {function(PlottrState): (String | Null)}
 */
export const selectedRelationshipTypeSelector = createSelector(
  familyTreeUIStateSelector,
  ({ selectedRelationshipType }) => {
    return selectedRelationshipType === '' ? '' : selectedRelationshipType || null
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const showRelationshipColourPickerSelector = createSelector(
  familyTreeUIStateSelector,
  ({ showColourPicker }) => {
    return showColourPicker || false
  }
)

/**
 * @type {function(PlottrState): FamilyTreeStagePosition}
 */
const familyTreePositionsSelector = createSelector(
  familyTreeUIStateSelector,
  currentFamilyTreeIdSelector,
  (familyTreeState, familyTreeId) => {
    if (typeof familyTreeId === 'number') {
      return familyTreeState?.stagePositions?.[familyTreeId] ?? initialFamilyTreePosition
    } else {
      return initialFamilyTreePosition
    }
  }
)

/**
 * Produce a number which scales the dimensions of the displayed
 * family tree by multiplication.
 *
 * @type {function(PlottrState): number}
 */
export const familyTreeScaleSelector = createSelector(familyTreePositionsSelector, (position) => {
  return position?.scale ?? 1
})

/**
 * Produce the x and y offsets of the current family tree stage.
 *
 * @type {function(PlottrState): ({ x: number, y: number } | Null)}
 */
export const familyTreeStageOffsetSelector = createSelector(
  familyTreePositionsSelector,
  (position) => {
    if (typeof position.x !== 'number' || typeof position.y !== 'number') {
      return null
    } else {
      return { x: position.x, y: position.y }
    }
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const arePreviewPanelsVisibleSelector = createSelector(
  uiSelector,
  ({ showPreviewPanels }) => {
    return showPreviewPanels ?? true
  }
)

/**
 * @type {function(PlottrState): Bookmarks}
 */
export const bookmarksSelector = createSelector(uiSelector, ({ bookmarks }) => {
  return bookmarks ?? { items: [], collapsedSection: [] }
})

/**
 * @type {function(PlottrState): Array<BookmarkItem>}
 */
export const allBookmarkedItemsSelector = createSelector(
  bookmarksSelector,
  allCardsSelector,
  allCharactersSelector,
  allPlacesSelector,
  allNotesSelector,
  allBooksAsArraySelector,
  ({ items }, cards, characters, places, notes, books) => {
    if (items?.length) {
      return items.map((b) => {
        switch (b.kind) {
          case 'cards': {
            const card = cards.find((x) => x.id == b.id)
            const book = books.find(
              (book) =>
                // NOTE: this check uses the weak comparison operator
                // deliberately.  Note that book.id is a number(!)
                //
                // @ts-ignore
                book.id == b.bookId
            )

            return {
              ...b,
              title: card?.title ?? 'Untitled',
              bookTitles: book?.title ? [book?.title] : [],
            }
          }
          case 'notes': {
            const note = notes.find((x) => x.id == b.id)
            const bookTitles = (note?.bookIds ?? []).reduce((acc, id) => {
              const book = books.find((b) => b.id == id)
              if (book) {
                return [...acc, book?.title ?? 'Untitled']
              } else {
                return acc
              }
            }, [])

            return {
              ...b,
              title: note?.title ?? 'Untitled',
              bookTitles,
            }
          }

          case 'places': {
            const place = places.find((x) => x.id == b.id)
            const bookTitles = (place?.bookIds ?? []).reduce((acc, id) => {
              const book = books.find((b) => b.id == id)
              if (book) {
                return [...acc, book?.title ?? 'Untitled']
              } else {
                return acc
              }
            }, [])

            return {
              ...b,
              title: place?.name ?? 'Unnamed',
              bookTitles,
            }
          }
          case 'characters': {
            const character = characters.find((x) => x.id == b.id)
            const bookTitles = (character?.bookIds ?? []).reduce((acc, id) => {
              const book = books?.find((b) => b.id == id)
              if (book) {
                return [...acc, book?.title ?? 'Untitled']
              } else {
                return acc
              }
            }, [])

            return {
              ...b,
              title: character?.name ?? 'Unnamed',
              bookTitles,
            }
          }

          default:
            return b
        }
      })
    } else {
      return []
    }
  }
)

/**
 * @type {function(PlottrState): Array<BookmarkEntityKind>}
 */
export const bookmarkCollapsedEntitySelector = createSelector(
  bookmarksSelector,
  ({ collapsedSection }) => collapsedSection ?? []
)

/**
 * @type {function(PlottrState): Record<BookmarkEntityKind, Array<BookmarkItem>> | {}}
 */
export const bookmarksByKindSelector = createSelector(allBookmarkedItemsSelector, (bookmarks) => {
  return groupBy(bookmarks, 'kind') ?? {}
})

/**
 * @type {function(PlottrState): String}
 */
export const currentBookTitleSelector = createSelector(
  allBooksSelector,
  currentTimelineSelector,
  (books, bookId) => {
    const book = books[bookId]
    if (!Array.isArray(book)) {
      return book.title
    } else {
      return ''
    }
  }
)

/**
 * @type {function(PlottrState): Array<Note>}
 */
export const allNotesInBookSelector = createSelector(
  allNotesSelector,
  currentTimelineSelector,
  (notes, bookId) =>
    notes.filter((note) => {
      if (note.bookIds.length === 0) return true
      if (note.bookIds.some(isSeries)) return true
      return note.bookIds.includes(bookId)
    })
)

/**
 * @type {function(PlottrState): AttributeTab}
 */
export const attributeTabsSelector = createSelector(uiSelector, ({ attributeTabs }) => {
  return attributeTabs || {}
})

/**
 * @type {function(PlottrState): boolean}
 */
export const showBookTabsSelector = createSelector(
  allBookIdsSelector,
  allCharactersSelector,
  showBookTabs
)

/**
 * @type {function(PlottrState): ('all' | number)}
 */
export const selectedCharacterAttributeTabSelector = createSelector(
  attributeTabsSelector,
  showBookTabsSelector,
  ({ characters }, showTabs) => {
    return showTabs ? characters || 'all' : 'all'
  }
)

/**
 * @type {function(PlottrState): Array<Attribute | LegacyCustomAttribute>}
 */
export const characterAttributesForCurrentBookSelector = createSelector(
  attributesSelector,
  characterCustomAttributesSelector,
  selectedCharacterAttributeTabSelector,
  characterCustomAttributeOrderSelector,
  (attributes, legacyAttributes, bookId, order) => {
    const bookAttributes = (attributes && attributes.characters) || []
    const newAttributes = bookAttributes.filter((attribute) => {
      return attribute.type !== 'base-attribute'
    })
    const ordered = order.map((entry) => {
      if ('type' in entry && entry.type === 'attributes') {
        return newAttributes.find(({ id }) => {
          return id === entry.id
        })
      } else if (entry && 'name' in entry) {
        return legacyAttributes.find(({ name }) => {
          return name === entry.name
        })
      } else {
        return null
      }
    })
    const missing = differenceWith([...newAttributes, ...legacyAttributes], ordered, isEqual)
    return [...ordered, ...missing].filter(Boolean)
  }
)

/**
 * @type {function(PlottrState, number | 'series'): String}
 */
export const currentBookTitleForBookSelector = createSelector(
  allBooksSelector,
  bookIdSelector,
  currentTimelineSelector,
  (books, mobileBookId, bookId) => {
    if (mobileBookId && mobileBookId === 'series') {
      return 'Series'
    } else {
      const book = books[mobileBookId] ?? books[bookId]
      if (!Array.isArray(book)) {
        return book.title
      } else {
        return ''
      }
    }
  }
)
/**
 * @type {function(PlottrState): PlottrThemes} PlottrThemes
 */
export const themeSelector = createSelector(uiSelector, ({ theme: _theme }) => null)
